import { BatchItem } from "@rpldy/uploady";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  documentStatusUploadCompleteUpdate,
  useDocumentRetrieve,
} from "gen/endpoints/documents/documents";
import {
  getNoticeConfigRetrieveQueryKey,
  noticeConfigRetrieve,
  useUpsertNoticeConfig,
} from "gen/endpoints/notice-config/notice-config";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { NoticeConfigUpsertDataRequest } from "gen/model/noticeConfigUpsertDataRequest";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { compact, get } from "lodash-es";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import {
  BooleanToggleButtonGroup,
  BooleanToggleButtonGroupValue,
} from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import { DocumentList } from "shared/components/document/DocumentList";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { FileUpload } from "shared/components/upload/FileUpload";
import { uploadAcceptImagePNG } from "shared/components/upload/FileUploadUtils";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useHasPermission } from "shared/utils/PermissionUtils";

type NoticeEnableFieldName = "is_enabled" | "ilpa_notice_workbook_is_enabled";

export const FundFamilySetupNoticesTabConfig: React.FC = () => {
  const { t } = useTranslation(["notices", "common"]);
  const { fundFamilyId } = useRequiredStringParams();
  const hasConfigPermission = useHasPermission(PermissionTypeEnum.config);

  const defaultNoticeConfigData: NoticeConfigUpsertDataRequest = {
    fund_family_id: fundFamilyId,
    logo_id: undefined,
    signature_id: undefined,
    is_enabled: false,
  };

  const queryClient = useQueryClient();
  const { data, refetch: refetchNoticeConfig } = useQuery({
    queryKey: [fundFamilyId],
    queryFn: async () => await noticeConfigRetrieve(fundFamilyId),
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

  const noticeConfigData = data || defaultNoticeConfigData;

  const { mutateAsync: upsertNoticeConfig } = useUpsertNoticeConfig();

  const { data: logoDocument, refetch: refetchLogoDocument } =
    useDocumentRetrieve(noticeConfigData.logo_id!, {
      query: { throwOnError: false, enabled: !!noticeConfigData.logo_id },
    });
  const { data: signatureDocument, refetch: refetchSignatureDocument } =
    useDocumentRetrieve(noticeConfigData.signature_id!, {
      query: {
        throwOnError: false,
        enabled: !!noticeConfigData.signature_id,
      },
    });

  const refetchDocuments = useCallback(async () => {
    await refetchNoticeConfig();
    await Promise.all([refetchLogoDocument(), refetchSignatureDocument()]);
  }, [refetchNoticeConfig, refetchLogoDocument, refetchSignatureDocument]);

  const toggleEnabled = useCallback(
    (field_name: NoticeEnableFieldName) =>
      async (value: BooleanToggleButtonGroupValue) => {
        const data: NoticeConfigUpsertDataRequest = {
          ...noticeConfigData,
          // The [var] syntax dynamically computes the key name
          [field_name]: value as boolean,
        };
        await upsertNoticeConfig({
          fundFamilyId,
          data,
        });

        await Promise.all([
          refetchNoticeConfig(),
          queryClient.invalidateQueries({
            queryKey: getNoticeConfigRetrieveQueryKey(fundFamilyId),
          }),
        ]);
      },
    [
      fundFamilyId,
      noticeConfigData,
      upsertNoticeConfig,
      refetchNoticeConfig,
      queryClient,
    ],
  );
  const onEnabledToggled = useCallback(
    async (value: BooleanToggleButtonGroupValue) => {
      await toggleEnabled("is_enabled")(value);
    },
    [toggleEnabled],
  );
  const onILPAEnabledToggled = useCallback(
    async (value: BooleanToggleButtonGroupValue) => {
      await toggleEnabled("ilpa_notice_workbook_is_enabled")(value);
    },
    [toggleEnabled],
  );

  const onLogoItemFinished = useCallback(
    async (item: BatchItem) => {
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const document: DocumentCreateResponseData = get(item, "document")!;
      await documentStatusUploadCompleteUpdate(document.id);
      const data: NoticeConfigUpsertDataRequest = {
        ...noticeConfigData,
        fund_family_id: fundFamilyId,
        logo_id: document.id,
      };
      await upsertNoticeConfig({
        fundFamilyId,
        data,
      });
      await refetchDocuments();
    },
    [fundFamilyId, noticeConfigData, upsertNoticeConfig, refetchDocuments],
  );

  const onSignatureUploadItemFinished = useCallback(
    async (item: BatchItem) => {
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const document: DocumentCreateResponseData = get(item, "document")!;
      await documentStatusUploadCompleteUpdate(document.id);
      const data: NoticeConfigUpsertDataRequest = {
        ...noticeConfigData,
        fund_family_id: fundFamilyId,
        signature_id: document.id,
      };
      await upsertNoticeConfig({
        fundFamilyId,
        data,
      });
      await refetchDocuments();
    },
    [fundFamilyId, noticeConfigData, upsertNoticeConfig, refetchDocuments],
  );

  const [, setLogoUploadItem] = useState<BatchItem[]>([]);
  const [, setSignatureUploadItem] = useState<BatchItem[]>([]);

  return (
    <PageTabBody>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("notices:configurationTab.basicDetails")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <BooleanToggleButtonGroup
              title={t("notices:customNotices")}
              trueText={t("common:enabled")}
              falseText={t("common:disabled")}
              isDisabled={!hasConfigPermission}
              onChange={onEnabledToggled}
              value={noticeConfigData.is_enabled}
              mb="16px"
            />
            <BooleanToggleButtonGroup
              title={t("notices:ilpaNoticeWorkbook.title")}
              trueText={t("common:enabled")}
              falseText={t("common:disabled")}
              isDisabled={!hasConfigPermission}
              onChange={onILPAEnabledToggled}
              value={noticeConfigData.ilpa_notice_workbook_is_enabled}
              mb="16px"
            />
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("notices:configurationTab.logo")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <Restricted permission={PermissionTypeEnum.config}>
              <FileUpload
                documentType={DocumentTypeEnum.image}
                accept={
                  uploadAcceptImagePNG
                } /* Non PNG images not supported by anvil */
                onItemsChanged={setLogoUploadItem}
                onItemFinished={onLogoItemFinished}
                itemFilter={() => false}
              />
            </Restricted>
            <DocumentList
              documents={compact([logoDocument])}
              onDocumentDeleted={async () => await refetchDocuments()}
            />
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("notices:configurationTab.signature")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <Restricted permission={PermissionTypeEnum.config}>
              <FileUpload
                documentType={DocumentTypeEnum.image}
                accept={
                  uploadAcceptImagePNG
                } /* Non PNG images not supported by anvil */
                onItemsChanged={setSignatureUploadItem}
                onItemFinished={onSignatureUploadItemFinished}
                itemFilter={() => false}
              />
            </Restricted>
            <DocumentList
              documents={compact([signatureDocument])}
              onDocumentDeleted={async () => await refetchDocuments()}
            />
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
    </PageTabBody>
  );
};
